/**
 * Mixpanel Analytics Module for Web
 *
 * Initialize Mixpanel for browser analytics tracking.
 * Import this module in main.tsx to initialize on app start.
 *
 * @see docs/ANALYTICS_DESIGN.md for event naming and schema
 */

import mixpanel from 'mixpanel-browser';

// Mixpanel Project Token
const MIXPANEL_TOKEN = '9b3b8f516dff60302c74f50892989194';

// Environment detection
const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;

/**
 * Initialize Mixpanel with configuration
 * - autocapture: disabled (manual tracking only)
 * - record_sessions_percent: 0 (no session replay)
 */
mixpanel.init(MIXPANEL_TOKEN, {
  autocapture: false,
  record_sessions_percent: 0,
  debug: isDev,
  track_pageview: false, // Manual page tracking
  persistence: 'localStorage',
});

/**
 * Register super properties (sent with every event)
 * @see docs/ANALYTICS_DESIGN.md Section 3
 */
mixpanel.register({
  platform: 'web',
  env: isProd ? 'prod' : 'dev',
  release_stage: 'mvp',
  web_version: import.meta.env.VITE_APP_VERSION || '0.0.1',
});

// Import event definitions
import {
  AnalyticsEvents,
  type UploadEventProperties,
  type TaskEventProperties,
  type TaskAbandonedEventProperties,
  type TaskRetriedEventProperties,
  type LearningEventProperties,
  type ConversationEventProperties,
  type FeedbackEventProperties,
} from './analytics.events';

/**
 * Track session_started on module initialization
 * This fires once per page load/refresh, providing accurate DAU/WAU metrics
 */
mixpanel.track(AnalyticsEvents.SESSION_STARTED, {
  referrer: document.referrer || undefined,
  url_path: window.location.pathname,
});

// Export event names for convenience
export { AnalyticsEvents } from './analytics.events';

// Export analytics API
export const analytics = {
  /**
   * Get the raw Mixpanel instance for advanced usage
   */
  getInstance: () => mixpanel,

  /**
   * Identify a user - call after login
   * @param userId - Unique user identifier
   */
  identify: (userId: string) => {
    mixpanel.identify(userId);
  },

  /**
   * Reset user identity - call on logout
   */
  reset: () => {
    mixpanel.reset();
    mixpanel.track(AnalyticsEvents.AUTH_LOGOUT);
  },

  /**
   * Set user profile properties
   * @param properties - User properties to set
   */
  setUserProperties: (properties: Record<string, unknown>) => {
    mixpanel.people.set(properties);
  },

  /**
   * Track an event
   * @param eventName - Name of the event (snake_case)
   * @param properties - Optional event properties
   */
  track: (eventName: string, properties?: Record<string, unknown>) => {
    mixpanel.track(eventName, properties);
  },

  /**
   * Track a timed event (call track with same name to complete)
   * @param eventName - Name of the event to time
   */
  timeEvent: (eventName: string) => {
    mixpanel.time_event(eventName);
  },

  /**
   * Register super properties (sent with every event)
   * @param properties - Properties to register
   */
  registerSuperProperties: (properties: Record<string, unknown>) => {
    mixpanel.register(properties);
  },

  /**
   * Register super properties once (only if not already set)
   * @param properties - Properties to register
   */
  registerSuperPropertiesOnce: (properties: Record<string, unknown>) => {
    mixpanel.register_once(properties);
  },

  /**
   * Opt user out of tracking
   */
  optOut: () => {
    mixpanel.opt_out_tracking();
  },

  /**
   * Opt user in to tracking
   */
  optIn: () => {
    mixpanel.opt_in_tracking();
  },

  // ==================== Convenience Methods ====================

  // Upload Events
  trackUploadStarted: (props: UploadEventProperties) => {
    mixpanel.track(AnalyticsEvents.UPLOAD_STARTED, props);
  },

  trackUploadCompleted: (props: UploadEventProperties) => {
    mixpanel.track(AnalyticsEvents.UPLOAD_COMPLETED, props);
  },

  trackUploadFailed: (props: UploadEventProperties) => {
    mixpanel.track(AnalyticsEvents.UPLOAD_FAILED, props);
  },

  // Task Events
  trackTaskSummaryShown: (props: TaskEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_SUMMARY_SHOWN, props);
  },

  trackTaskSummaryConfirmed: (props: TaskEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_SUMMARY_CONFIRMED, props);
  },

  trackTaskOpened: (props: TaskEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_OPENED, props);
  },

  // Learning Events
  trackLessonStarted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.LESSON_STARTED, props);
  },

  trackLessonCompleted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.LESSON_COMPLETED, props);
  },

  trackExerciseAnswered: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.EXERCISE_ANSWERED, props);
  },

  trackExerciseSetCompleted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.EXERCISE_SET_COMPLETED, props);
  },

  trackAssessmentStarted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.ASSESSMENT_STARTED, props);
  },

  trackAssessmentAnswered: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.ASSESSMENT_ANSWERED, props);
  },

  trackAssessmentCompleted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.ASSESSMENT_COMPLETED, props);
  },

  // Task Events (extended)
  trackTaskShared: (props: TaskEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_SHARED, props);
  },

  trackTaskCompleted: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_COMPLETED, props);
  },

  trackTaskAbandoned: (props: TaskAbandonedEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_ABANDONED, props);
  },

  trackTaskRetried: (props: TaskRetriedEventProperties) => {
    mixpanel.track(AnalyticsEvents.TASK_RETRIED, props);
  },

  // Learning Events (extended)
  trackExerciseHintViewed: (props: LearningEventProperties) => {
    mixpanel.track(AnalyticsEvents.EXERCISE_HINT_VIEWED, props);
  },

  // Conversation Events
  trackConversationStarted: (props: ConversationEventProperties) => {
    mixpanel.track(AnalyticsEvents.CONVERSATION_STARTED, props);
  },

  trackMessageSent: (props: ConversationEventProperties) => {
    mixpanel.track(AnalyticsEvents.MESSAGE_SENT, props);
  },

  trackActionClicked: (props: ConversationEventProperties) => {
    mixpanel.track(AnalyticsEvents.ACTION_CLICKED, props);
  },

  // Onboarding Events
  trackOnboardingCompleted: (props: { steps_completed?: number }) => {
    mixpanel.track(AnalyticsEvents.ONBOARDING_COMPLETED, props);
  },

  trackChildProfileSelected: (props: { child_id?: string; nickname?: string; grade?: string }) => {
    mixpanel.track(AnalyticsEvents.CHILD_PROFILE_SELECTED, props);
  },

  // TTS Events
  trackTtsPlayed: (props: { audio_type?: string; duration_ms?: number }) => {
    mixpanel.track(AnalyticsEvents.TTS_PLAYED, props);
  },

  // Feedback Events
  trackFeedbackSubmitted: (props: FeedbackEventProperties) => {
    mixpanel.track(AnalyticsEvents.FEEDBACK_SUBMITTED, props);
  },

  // Note: trackError removed - use Sentry for error tracking
  // import * as Sentry from '@sentry/react';
  // Sentry.captureException(error);
};

export default analytics;
